import clsx from 'clsx';

import {ValidColorT} from '@/lib/types/colors.types';
import {AnyIconNameT} from '@/lib/types/icons/libraries.types';
import {NotificationType, ToastPropsT} from '@/lib/types/notifications';

import Icon from '@/components/icon';

export const TOAST_TYPES = ['info', 'success', 'warning', 'error'] as const;

const DEFAULT_ICONS: Record<NotificationType, AnyIconNameT> = {
  info: 'info-circle',
  success: 'circle-check',
  warning: 'alert-triangle',
  error: 'alert-circle',
};

const TYPE_TO_COLOR: Record<NotificationType, ValidColorT> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'danger',
};

export function InfoToast(props: Omit<ToastPropsT, 'type'>) {
  return <BaseToast {...props} type="info" />;
}

export function SuccessToast(props: Omit<ToastPropsT, 'type'>) {
  return <BaseToast {...props} type="success" />;
}

export function WarningToast(props: Omit<ToastPropsT, 'type'>) {
  return <BaseToast {...props} type="warning" />;
}

export function ErrorToast(props: Omit<ToastPropsT, 'type'>) {
  return <BaseToast {...props} type="error" />;
}

function BaseToast({
  title,
  message,
  link,
  linkText,
  openInNewTab,
  type = 'info',
  icon,
  actions,
  closeToast,
  color,
}: ToastPropsT) {
  const iconName = icon || DEFAULT_ICONS[type];
  const iconColor = color || TYPE_TO_COLOR[type];

  return (
    <div className="flex select-none items-stretch rounded-t-lg border border-gray-700 bg-toast-body-dark p-3">
      <div
        className={clsx('w-1 rounded-full', {
          'bg-info': type === 'info',
          'bg-success': type === 'success',
          'bg-warning': type === 'warning',
          'bg-danger': type === 'error',
        })}
      />

      <div className="flex items-stretch gap-3">
        <div
          className={clsx('ml-2 flex flex-shrink-0 items-center justify-center', {
            'text-info': type === 'info',
            'text-success': type === 'success',
            'text-warning': type === 'warning',
            'text-danger': type === 'error',
          })}
        >
          <Icon name={iconName} color={iconColor} size={36} />
        </div>
        <div className="">
          <p className="font-medium text-white">{title}</p>
          <p className="mt-0.5 text-sm text-gray-400">{message}</p>
          {link && (
            <a
              href={link}
              className="mt-2 flex items-center gap-1 text-sm text-info hover:text-info/80"
              {...(openInNewTab ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
            >
              {linkText || 'View Details'}
              <Icon name="arrow-right" size={16} />
            </a>
          )}
          {actions && actions.length > 0 && (
            <div className="mt-3 flex gap-2">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={clsx('rounded-md px-3 py-1 text-sm transition-colors', {
                    'bg-info text-white': (!action.variant && type === 'info') || action.variant === 'info-full',
                    'bg-info/10 text-info hover:bg-info hover:text-white': action.variant === 'info',
                    'bg-success text-white':
                      (!action.variant && type === 'success') || action.variant === 'success-full',
                    'bg-success/10 text-success hover:bg-success hover:text-white': action.variant === 'success',
                    'bg-warning text-white':
                      (!action.variant && type === 'warning') || action.variant === 'warning-full',
                    'bg-warning/10 text-warning hover:bg-warning hover:text-white': action.variant === 'warning',
                    'bg-danger text-white': (!action.variant && type === 'error') || action.variant === 'danger-full',
                    'bg-danger/10 text-danger hover:bg-danger hover:text-white': action.variant === 'danger',
                    'bg-primary text-white': action.variant === 'primary-full',
                    'bg-primary/10 text-primary hover:bg-primary hover:text-white': action.variant === 'primary',
                    'bg-secondary text-white': action.variant === 'secondary-full',
                    'bg-secondary/10 text-secondary hover:bg-secondary hover:text-white':
                      action.variant === 'secondary',
                    'bg-light text-defaulttextcolor hover:bg-light/90': action.variant === 'light',
                    'bg-gray-900 text-white hover:bg-gray-900/90 dark:bg-white dark:text-black':
                      action.variant === 'dark',
                  })}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          <button type="button" className="absolute right-2 top-2" onClick={closeToast}>
            <Icon name="x" size={16} className="text-gray-400 hover:text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
